import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  KycError,
  KycErrorCode,
  KycProviderType,
  ProcessingStep,
  PROCESSING_STEPS,
} from '../kyc-upload.types';

@Injectable({
  providedIn: 'root',
})
export class KycMessageService {
  constructor(private translate: TranslateService) {}

  /**
   * Get provider-specific privacy notice
   */
  getProviderPrivacyNotice(provider: KycProviderType): string {
    const notices: Record<KycProviderType, string> = {
      thirdfort: this.translate.instant('KYCProviderPrivacyNoticeThirdfort'),
      veriff: this.translate.instant('KYCProviderPrivacyNoticeVeriff'),
      verifflite: this.translate.instant('KYCProviderPrivacyNoticeVeriffLite'),
      // somoskyc_v1 is a Veriff Lite clone — reuses its copy until dedicated strings are added.
      somoskyc_v1: this.translate.instant('KYCProviderPrivacyNoticeVeriffLite'),
      textract: this.translate.instant('KYCProviderPrivacyNoticeTextract'),
      mock: this.translate.instant('KYCProviderPrivacyNoticeMock'),
    };

    return notices[provider] || notices.thirdfort;
  }

  /**
   * Get processing step message. Keyed by the generic (non-SomosKycV1) steps only — the
   * SomosV1-only READING/CONFIRMING keys have no entry in PROCESSING_STEPS.
   */
  getProcessingMessage(step: keyof typeof PROCESSING_STEPS): string {
    return PROCESSING_STEPS[step];
  }

  /**
   * Get all processing steps for display
   */
  getProcessingSteps(): Array<{ step: ProcessingStep; message: string }> {
    return Object.entries(PROCESSING_STEPS).map(([step, message]) => ({
      step: step as ProcessingStep,
      message,
    }));
  }

  /**
   * Get error details with user-friendly message and suggested action
   */
  getErrorDetails(
    errorCode: KycErrorCode,
    technicalDetails?: string
  ): KycError {
    const errorMap: Record<KycErrorCode, Omit<KycError, 'technicalDetails'>> = {
      FILE_TOO_LARGE: {
        code: 'FILE_TOO_LARGE',
        message: this.translate.instant('KYC.ERROR.FILE_TOO_LARGE'),
        suggestedAction:
          technicalDetails ??
          this.translate.instant('KYC.ERROR.FILE_TOO_LARGE_ACTION', {
            size: 50,
          }),
        canRetry: true,
      },

      FILE_INVALID_TYPE: {
        code: 'FILE_INVALID_TYPE',
        message: this.translate.instant('KYC.ERROR.FILE_INVALID_TYPE'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.FILE_INVALID_TYPE_ACTION'
        ),
        canRetry: true,
      },

      FILE_CORRUPTED: {
        code: 'FILE_CORRUPTED',
        message: this.translate.instant('KYC.ERROR.FILE_CORRUPTED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.FILE_CORRUPTED_ACTION'
        ),
        canRetry: true,
      },

      IMAGE_RESOLUTION_TOO_LOW: {
        code: 'IMAGE_RESOLUTION_TOO_LOW',
        message: this.translate.instant('KYC.ERROR.IMAGE_RESOLUTION_TOO_LOW'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.IMAGE_RESOLUTION_TOO_LOW_ACTION'
        ),
        canRetry: true,
      },

      UPLOAD_FAILED: {
        code: 'UPLOAD_FAILED',
        message: this.translate.instant('KYC.ERROR.UPLOAD_FAILED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.UPLOAD_FAILED_ACTION'
        ),
        canRetry: true,
      },

      UPLOAD_TIMEOUT: {
        code: 'UPLOAD_TIMEOUT',
        message: this.translate.instant('KYC.ERROR.UPLOAD_TIMEOUT'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.UPLOAD_TIMEOUT_ACTION'
        ),
        canRetry: true,
      },

      NETWORK_ERROR: {
        code: 'NETWORK_ERROR',
        message: this.translate.instant('KYC.ERROR.NETWORK_ERROR'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.NETWORK_ERROR_ACTION'
        ),
        canRetry: true,
      },

      SERVER_ERROR: {
        code: 'SERVER_ERROR',
        message: this.translate.instant('KYC.ERROR.SERVER_ERROR'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.SERVER_ERROR_ACTION'
        ),
        canRetry: true,
      },

      PROVIDER_ERROR: {
        code: 'PROVIDER_ERROR',
        message: this.translate.instant('KYC.ERROR.PROVIDER_ERROR'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.PROVIDER_ERROR_ACTION'
        ),
        canRetry: true,
      },

      PROCESSING_TIMEOUT: {
        code: 'PROCESSING_TIMEOUT',
        message: this.translate.instant('KYC.ERROR.PROCESSING_TIMEOUT'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.PROCESSING_TIMEOUT_ACTION'
        ),
        canRetry: false,
      },

      DOCUMENT_REJECTED: {
        code: 'DOCUMENT_REJECTED',
        message: this.translate.instant('KYC.ERROR.DOCUMENT_REJECTED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.DOCUMENT_REJECTED_ACTION'
        ),
        canRetry: true,
      },

      DOCUMENT_UNCLEAR: {
        code: 'DOCUMENT_UNCLEAR',
        message: this.translate.instant('KYC.ERROR.DOCUMENT_UNCLEAR'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.DOCUMENT_UNCLEAR_ACTION'
        ),
        canRetry: true,
      },

      DOCUMENT_EXPIRED: {
        code: 'DOCUMENT_EXPIRED',
        message: this.translate.instant('KYC.ERROR.DOCUMENT_EXPIRED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.DOCUMENT_EXPIRED_ACTION'
        ),
        canRetry: true,
      },

      VALIDATION_FAILED: {
        code: 'VALIDATION_FAILED',
        message: this.translate.instant('KYC.ERROR.VALIDATION_FAILED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.VALIDATION_FAILED_ACTION'
        ),
        canRetry: true,
      },

      RETRY_LIMIT_EXCEEDED: {
        code: 'RETRY_LIMIT_EXCEEDED',
        message: this.translate.instant('KYC.ERROR.RETRY_LIMIT_EXCEEDED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.RETRY_LIMIT_EXCEEDED_ACTION'
        ),
        canRetry: false,
      },

      USER_BLOCKED: {
        code: 'USER_BLOCKED',
        message: this.translate.instant('KYC.ERROR.USER_BLOCKED'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.USER_BLOCKED_ACTION'
        ),
        canRetry: false,
      },

      LEGAL_NAME_MISMATCH: {
        code: 'LEGAL_NAME_MISMATCH',
        message: this.translate.instant('KYC.ERROR.LEGAL_NAME_MISMATCH'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.LEGAL_NAME_MISMATCH_ACTION'
        ),
        canRetry: true,
      },

      UNKNOWN_ERROR: {
        code: 'UNKNOWN_ERROR',
        message: this.translate.instant('KYC.ERROR.UNKNOWN_ERROR'),
        suggestedAction: this.translate.instant(
          'KYC.ERROR.UNKNOWN_ERROR_ACTION'
        ),
        canRetry: true,
      },
    };

    const error = errorMap[errorCode] || errorMap.UNKNOWN_ERROR;

    return {
      ...error,
      technicalDetails,
    };
  }

  /**
   * Get success message based on verification status
   */
  getSuccessMessage(
    status: 'PROCESSING' | 'VERIFIED',
    provider: KycProviderType
  ): string {
    if (status === 'PROCESSING') {
      return this.translate.instant('KYC.SUCCESS.PROCESSING');
    }

    // VERIFIED
    const messages: Record<KycProviderType, string> = {
      thirdfort: this.translate.instant('KYC.SUCCESS.VERIFIED_THIRDFORT'),
      veriff: this.translate.instant('KYC.SUCCESS.VERIFIED_VERIFF'),
      verifflite: this.translate.instant('KYC.SUCCESS.VERIFIED_VERIFFLITE'),
      // somoskyc_v1 is a Veriff Lite clone — reuses its copy until dedicated strings are added.
      somoskyc_v1: this.translate.instant('KYC.SUCCESS.VERIFIED_VERIFFLITE'),
      textract: this.translate.instant('KYC.SUCCESS.VERIFIED_TEXTRACT'),
      mock: this.translate.instant('KYC.SUCCESS.VERIFIED_MOCK'),
    };

    return messages[provider] || messages.thirdfort;
  }

  /**
   * Get pending processing message
   */
  getPendingMessage(): string {
    return this.translate.instant('KYC.SUCCESS.PENDING');
  }

  /**
   * Get next steps message after successful upload
   */
  getNextStepsMessage(): string {
    return this.translate.instant('KYC.SUCCESS.NEXT_STEPS');
  }

  /**
   * Parse backend error to KycError
   */
  parseBackendError(error: any): KycError {
    // Try to extract meaningful error information
    let errorCode: KycErrorCode = 'UNKNOWN_ERROR';
    let technicalDetails = '';

    if (error?.error?.code) {
      errorCode = error.error.code as KycErrorCode;
    } else if (error?.status === 408 || error?.status === 504) {
      errorCode = 'UPLOAD_TIMEOUT';
    } else if (error?.status >= 500) {
      errorCode = 'SERVER_ERROR';
    } else if (error?.status === 0) {
      errorCode = 'NETWORK_ERROR';
    }

    if (error?.error?.message) {
      technicalDetails = error.error.message;
    } else if (error?.message) {
      technicalDetails = error.message;
    }

    return this.getErrorDetails(errorCode, technicalDetails);
  }
}
